/**
 * Rate Card — pure model helpers. 13-ui-screens.md §8.
 *
 * Everything in this file is data-shaping only: no React, no fetch. It lives
 * under `-lib/` so the TanStack Router generator ignores it (the `-` prefix is
 * `routeFileIgnorePrefix`), which is why a non-route module can sit inside
 * `src/routes/`.
 *
 * TYPES ARE NEVER REDECLARED. `WorkTypeListItemDto`, `RateHistoryEntryDto`,
 * `RateChangeRequestDto`, `UnitOfMeasure`, `RateKind` all come from
 * `@cropmate/shared`. The only local declarations are the `error.meta` shapes,
 * which the contract types as `Record<string, unknown>`. (The unit label maps
 * that used to sit here are F37 data now — see §1.)
 */
import type {
  RateHistoryEntryDto,
  RateKind,
  UnitOfMeasure,
  WorkTypeListItemDto,
} from '@cropmate/shared';
import { UNIT_CODE_PATTERN } from '@cropmate/shared';
import type { Filters } from '@/api/queries';
import { useEffect, useState } from 'react';
import Decimal from 'decimal.js';
import { formatDate } from '@/lib/dates';
import { formatMoney } from '@/lib/money';

/* ══════════════════════════ 1. Units of measure ═════════════════════════ */

/*
 * The `UNIT_SHORT` / `UNIT_LONG` maps that lived here are gone (ADR-0017): a unit is a row an
 * Admin adds at Admin → Configuration → Units, so a map in this bundle would be stale the
 * moment they did. Labels come from `lib/units.ts`'s `useUnitLabels()`, over F37. All this
 * file still knows about a unit is the SHAPE of its code, for the `?unit=` search param.
 */
function isUnit(value: unknown): value is UnitOfMeasure {
  return typeof value === 'string' && UNIT_CODE_PATTERN.test(value);
}

/* ═════════════════════════ 2. The search params ═════════════════════════ */

/**
 * `WorkTypeListItemDto.status` is the API's own union. `ANY` is a UI-only
 * sentinel meaning "do not filter"; it is never sent to the server.
 */
export type StatusFilter = 'ANY' | WorkTypeListItemDto['status'];

export const STATUS_FILTERS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'ANY', label: 'Any status' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'NO_RATE', label: 'No rate' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const STATUS_VALUES = STATUS_FILTERS.map((s) => s.value);

/** Both `/rates` and `/rates/$workTypeId` carry exactly this search state. */
export interface RatesSearch {
  /** Free text over code and name — R1 `q`. */
  q?: string;
  /** R1 `unit`. Single-select; the DTO accepts an array, we send one value. */
  unit?: UnitOfMeasure;
  /** Applied client-side — R1 has no status filter (see `filterByStatus`). */
  status?: StatusFilter;
  /** `As at` — R1 `asOf`. Re-resolves every row's rate at that date. */
  asOf?: string;
  /** §8.4: the propose panel's open state lives in the URL as `?propose=1`. */
  propose?: boolean;
  /** Rate-history panel: R7 `kind`. Defaults to `PIECE`. */
  kind?: RateKind;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * Hand-written because `zod` is NOT a dependency of `apps/web` and the task
 * forbids adding one. The contract is the same: unknown keys are dropped, bad
 * values fall back to `undefined` rather than throwing the route.
 */
export function validateRatesSearch(search: Record<string, unknown>): RatesSearch {
  const unit = search['unit'];
  const status = search['status'];
  const asOf = str(search['asOf']);
  const kind = search['kind'];
  const propose = search['propose'];

  const out: RatesSearch = {};
  const q = str(search['q']);
  if (q !== undefined) out.q = q;
  if (isUnit(unit)) out.unit = unit;
  if (typeof status === 'string' && STATUS_VALUES.includes(status as StatusFilter)) {
    out.status = status as StatusFilter;
  }
  if (asOf !== undefined && ISO_DATE.test(asOf)) out.asOf = asOf;
  if (kind === 'PIECE' || kind === 'OVERRIDING') out.kind = kind;
  /* `?propose=1` from the spec, plus the round-trip form `?propose=true`. */
  if (propose === true || propose === 1 || propose === '1' || propose === 'true') {
    out.propose = true;
  }
  return out;
}

/* ═══════════════════ 3. Search state → endpoint queries ═════════════════ */

/**
 * R1 `GET /work-types`. `status` is deliberately absent — the endpoint does
 * not filter on it (verified against the running API), so it is applied
 * client-side. That is exact only because the read is EVERY page
 * (`qk.rates.workTypesAll`): this once said "a closed list of nine rows" and
 * asked for one page of 200, and live already holds 63.
 */
export function workTypeListFilters(search: RatesSearch): Filters {
  const f: Filters = {};
  if (search.q) f['q'] = search.q;
  if (search.unit) f['unit'] = search.unit;
  if (search.asOf) f['asOf'] = search.asOf;
  /* Soft-deleted / inactive types are hidden unless the user asks for them. */
  if (search.status === 'INACTIVE') f['includeInactive'] = true;
  return f;
}

/** R7 `GET /work-types/{id}/rates`. */
export function historyFilters(search: RatesSearch): Filters {
  return { kind: search.kind ?? 'PIECE', page: 1, pageSize: 200 };
}

export function filterByStatus(
  rows: readonly WorkTypeListItemDto[],
  status: StatusFilter | undefined,
): WorkTypeListItemDto[] {
  if (!status || status === 'ANY') return [...rows];
  return rows.filter((r) => r.status === status);
}

/* ═════════════════════ 4. Rate-history presentation ═════════════════════ */

export type HistoryTone = {
  /** The 9×9 gutter dot. */
  dot: string;
  /** `StatusPill` kind — the closed map in 14-theme.md §7. */
  pill: 'pending' | 'approved' | 'superseded';
  /** The word in the pill. Colour is never the only signal. */
  label: string;
  /** Whether the amount is rendered at full strength or muted. */
  muted: boolean;
  /** A pending proposal must be visually distinct from an in-force rate. */
  emphasis: 'proposal' | 'inForce' | 'past';
};

/**
 * §8.5's tag table, extended with `FUTURE` **[N]** — the prototype has no
 * approved-but-not-yet-in-force state because it predates the propose/approve
 * transaction writing a forward-dated window.
 */
export const HISTORY_TONE: Record<RateHistoryEntryDto['state'], HistoryTone> = {
  PENDING: {
    dot: 'var(--status-warning)',
    pill: 'pending',
    label: 'Pending',
    muted: false,
    emphasis: 'proposal',
  },
  CURRENT: {
    dot: 'var(--brand-primary)',
    pill: 'approved',
    label: 'Current',
    muted: false,
    emphasis: 'inForce',
  },
  FUTURE: {
    dot: 'var(--brand-accent)',
    pill: 'approved',
    label: 'Scheduled',
    muted: false,
    emphasis: 'inForce',
  },
  SUPERSEDED: {
    dot: 'var(--border-default)',
    pill: 'superseded',
    label: 'Superseded',
    muted: true,
    emphasis: 'past',
  },
};

/**
 * FR-3.6 — "the complete history of its rates **with the approving user**".
 * The wire carries `UserRefDto` (`id`, `displayName`, `email`) and no role,
 * so the prototype's parenthetical "(Finance)" cannot be reproduced without
 * inventing it. The approver's name and the approval date — the two facts
 * FR-3.6 actually names — are both present.
 */
export function historyAttribution(entry: RateHistoryEntryDto): string {
  const proposer = entry.proposedBy?.displayName;
  const approver = entry.approvedBy?.displayName;
  const approvedOn = entry.approvedAt ? formatDate(entry.approvedAt) : null;

  if (entry.state === 'PENDING') {
    return proposer
      ? `Proposed by ${proposer} · awaiting Finance`
      : 'Awaiting Finance approval';
  }
  if (proposer && approver) {
    return approvedOn
      ? `Proposed by ${proposer} · approved by ${approver}, ${approvedOn}`
      : `Proposed by ${proposer} · approved by ${approver}`;
  }
  if (approver) {
    return approvedOn ? `Approved by ${approver}, ${approvedOn}` : `Approved by ${approver}`;
  }
  if (entry.origin === 'SEED') {
    return 'Seeded at go-live — no approver on record.';
  }
  return 'No approver on record.';
}

/**
 * §8.5 **[N]**: a closed window reads `01 Jan 2026 – 30 Sep 2026`; an
 * open-ended one reads `01 Jul 2026 – ` with nothing after the dash, because
 * the database stores `NULL` and not a `9999-12-31` sentinel.
 *
 * `validToInclusive` is the display value (`validTo − 1 day`); `validTo`
 * itself is the EXCLUSIVE bound and must never be printed.
 */
export function historyWindow(entry: RateHistoryEntryDto): string {
  const from = formatDate(entry.validFrom);
  if (entry.validTo === null) return `${from} – `;
  return `${from} – ${formatDate(entry.validToInclusive ?? entry.validTo)}`;
}

/* ═══════════════════════════ 5. New-rate input ══════════════════════════ */

/** §8.4 validation: the money regex, sign included (negative rates are legal). */
export const RATE_INPUT_RE = /^-?\d{1,12}(\.\d{1,6})?$/;

export const RATE_FORMAT_HINT = 'Enter a rate, for example 4.45 or -1.50.';

/* ═══════════════ 6. ADR-0020 · job sheets already entered ═══════════════ */

/** `1 job line` · `3 job lines`. */
export function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** `on 13 Oct 2026` · `from 01 Oct 2026 to 13 Oct 2026` · `` when there is no range. */
export function dateSpan(first: string | null, last: string | null): string {
  if (first === null || last === null) return '';
  return first === last
    ? `on ${formatDate(first)}`
    : `from ${formatDate(first)} to ${formatDate(last)}`;
}

/**
 * `+RM 12.40` · `−RM 3.20`. A reprice's `valueChange` is a CHANGE, so its sign is the point —
 * and `formatMoney` deliberately never prints one (it brackets a negative instead).
 */
export function signedMoney(raw: string): string {
  const d = new Decimal(raw);
  return `${d.isNegative() ? '−' : '+'}${formatMoney(d.abs().toFixed(4))}`;
}

/** The value, once it has stopped changing for `ms` — so a dry run is not fired per keystroke. */
export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * `4.5000` → `4.50`, `0.8025` → `0.8025`: a stored rate as a person would retype it, for
 * prefilling an input. At least two decimals, trailing zeros beyond them dropped.
 */
export function plainRate(raw: string): string {
  return new Decimal(raw).toFixed(4).replace(/(\.\d{2}\d*?)0+$/, '$1');
}
